import { QueryCache } from "@dk/fetchkit"

/* The one shared SWR cache for storefront reads, and the boundary around it.
 *
 * Three callers now share it — the router's hold guard (lib/navhold), the link
 * prefetcher (lib/prefetch) and the Vue composable (lib/useQuery) — so the cache
 * instance and the KEY STRINGS both have to live in one place. A guard that warms
 * `product:x` while the page reads `products:x` would look like it worked and
 * silently never hit.
 *
 * The instance stays module-private: these functions ARE the encapsulation
 * boundary, so nothing outside this file can reach `.set()`, `.invalidate()`,
 * `.clear()` or the LRU. This file also deliberately imports nothing from Vue —
 * it is pulled into the entry chunk by the router, and useQuery (the Vue adapter)
 * stays in its own lazy chunk. */
const cache = new QueryCache()

/** 30s fresh window, then serve stale and revalidate. Same options for every
 *  caller so a warm read and a component read can never disagree about freshness. */
const OPTS = { ttl: 30_000, swr: true } as const

/** Cache keys. Shared so the guard, the prefetcher and the page composable can
 *  never drift onto three different strings for one product. */
export const qk = {
  product: (handle: string) => `product:${handle}`,
  related: (handle: string) => `related:${handle}`,
}

/** Warm the cache with no component attached — the router guard and the link
 *  prefetcher. Singleflighted by fetchkit, so a prefetch still in flight when the
 *  click lands is REUSED rather than duplicated. */
export function warmQuery<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  return cache.fetch(key, fetcher, OPTS)
}

/** Synchronous peek — the whole point of the hold. A warm key lets a page render
 *  real content on its FIRST frame, with no skeleton and no height collapse.
 *  `cache.fetch()` cannot do this: it is async, so even a fresh hit costs a
 *  microtask, which is one skeleton frame for data already in memory. */
export function peekQuery<T>(key: string): T | undefined {
  return cache.get<T>(key)
}

/** What a mounted component uses: same cache, same options, awaited. */
export function fetchQuery<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  return cache.fetch(key, fetcher, OPTS)
}
